import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Checkbox } from 'antd'
import { includes, isNil, map, orderBy, toString } from 'lodash'
import PropTypes from 'prop-types'

import { GridFormItem } from '../../../core/components/sections'
import { useQueryParams } from '../../../core/hooks'
import { getFilterOptions, isBooleanTrue } from '../../../core/utils'

import { Select } from '../../../common/components/sections'
import {
  StyledFullWidthSearchInput,
  StyledPaddingBottomRow,
} from '../../../common/components/styled'
import {
  ALL_SELECT_FILTER_ITEM,
  ALL_SELECT_OPTION_ITEM,
  PAGINATION,
} from '../../../common/constants'
import { findNodeByValue } from '../../../common/utils'

import {
  MenuCurationTaskCuratorFilter,
  MenuCurationTaskQaFilter,
} from '../../../menu-curation-tasks/components/sections'
import {
  CURATION_DETAIL_LEVEL,
  MENU_CURATION_TASK_STATUS,
} from '../../../menu-curation-tasks/constants'
import { useMenuTitleDishCountsLazyQuery } from '../../../menu-title/hooks'
import {
  DEFAULT_DISH_LIST_FILTERS,
  DISH_CERTAINTY_FILTER,
  DISH_IGNORED_FILTER,
  TIER_FILTER,
} from '../../constants'
import {
  useDishesUsedMetadataCountsQuery,
  useDishNamesQuery,
} from '../../hooks'

import DishMenuTitleFilter from './dish-menu-title-filter'

const DishFilters = ({
  isMenuCurationTaskDirty,
  curationDetailLevel,
  taskStatus,
  menuId,
}) => {
  const {
    hasErrors = DEFAULT_DISH_LIST_FILTERS.HAS_ERRORS,
    isAvailableForWork = DEFAULT_DISH_LIST_FILTERS.IS_AVAILABLE_FOR_WORK,
    wasDoneInPreviousTwoWeeks,
    isNotChecked = DEFAULT_DISH_LIST_FILTERS.IS_NOT_CHECKED,
    isDishTypeIgnored = DEFAULT_DISH_LIST_FILTERS.IGNORED,
    search = DEFAULT_DISH_LIST_FILTERS.SEARCH,
    menuTitleId = DEFAULT_DISH_LIST_FILTERS.MENU_TITLE,
    certainty = curationDetailLevel ===
    CURATION_DETAIL_LEVEL.PARTIAL_CURATION.value
      ? DISH_CERTAINTY_FILTER.LOW_CERTAINTY.value
      : DEFAULT_DISH_LIST_FILTERS.CERTAINTY,
    dishIds,
    tier = DEFAULT_DISH_LIST_FILTERS.TIER,
    curatorId = DEFAULT_DISH_LIST_FILTERS.CURATOR_ID,
    qaId = DEFAULT_DISH_LIST_FILTERS.QA_ID,
    dishTypeId = DEFAULT_DISH_LIST_FILTERS.DISH_TYPE_ID,
    courseTypeId = DEFAULT_DISH_LIST_FILTERS.COURSE_TYPE_ID,
    setQueryParams,
  } = useQueryParams()

  const { dishTypeOptions, courseTypeOptions } = useSelector(
    store => store.common.metadata.data,
  )

  const [dishIdValues, setDishIdValues] = useState()

  const isTaskInProgress = useMemo(
    () =>
      includes(
        [
          MENU_CURATION_TASK_STATUS.TIER_ONE_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.CURATION_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value,
        ],
        taskStatus,
      ),
    [taskStatus],
  )

  const selectedIsAvailableForWork = useMemo(
    () => (!isTaskInProgress ? null : isAvailableForWork),
    [isAvailableForWork, isTaskInProgress],
  )

  const selectedWasDoneInPreviousTwoWeeks = useMemo(() => {
    if (
      isNil(wasDoneInPreviousTwoWeeks) &&
      includes(
        [
          MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value,
        ],
        taskStatus,
      )
    ) {
      return true
    }

    return wasDoneInPreviousTwoWeeks
  }, [taskStatus, wasDoneInPreviousTwoWeeks])

  const { filters } = useMemo(
    () => ({
      filters: {
        isDishTypeIgnored,
        certainty,
        hasErrors,
        isAvailableForWork: selectedIsAvailableForWork,
        wasDoneInPreviousTwoWeeks: selectedWasDoneInPreviousTwoWeeks,
        isNotChecked,
        menuId,
        search: !isNil(search) ? toString(search) : null,
        dishIds,
        menuTitleId,
        tier,
        curatorId,
        qaId,
        dishTypeId,
        courseTypeId,
      },
    }),
    [
      isDishTypeIgnored,
      certainty,
      hasErrors,
      selectedIsAvailableForWork,
      selectedWasDoneInPreviousTwoWeeks,
      isNotChecked,
      menuId,
      search,
      dishIds,
      menuTitleId,
      tier,
      curatorId,
      qaId,
      dishTypeId,
      courseTypeId,
    ],
  )

  const { data: usedMetadataCounts = {} } =
    useDishesUsedMetadataCountsQuery(filters)

  const [fetchDishCounts] = useMenuTitleDishCountsLazyQuery()

  const { data: dishNames = [] } = useDishNamesQuery(filters)

  const dishTypeFilterOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...map(
        orderBy(usedMetadataCounts?.dishType, ['count'], ['desc']),
        ({ id, count }) => {
          const node = findNodeByValue(dishTypeOptions, id)

          return {
            label: `${node?.title} (${count})`,
            value: id,
          }
        },
      ),
    ],
    [dishTypeOptions, usedMetadataCounts?.dishType],
  )
  const courseTypeFilterOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...map(
        orderBy(usedMetadataCounts?.courseType, ['count'], ['desc']),
        ({ id, count }) => {
          const node = findNodeByValue(courseTypeOptions, id)

          return {
            label: `${node?.title} (${count})`,
            value: id,
          }
        },
      ),
    ],
    [courseTypeOptions, usedMetadataCounts?.courseType],
  )

  const dishNameOptions = useMemo(
    () => getFilterOptions(dishNames, 'name', 'id'),
    [dishNames],
  )

  const tierOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...getFilterOptions(TIER_FILTER, 'label', 'value'),
    ],
    [],
  )

  const ignoredOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...getFilterOptions(DISH_IGNORED_FILTER, 'label', 'value'),
    ],
    [],
  )

  const certaintyOptions = useMemo(
    () => [
      ALL_SELECT_OPTION_ITEM,
      ...getFilterOptions(DISH_CERTAINTY_FILTER, 'label', 'value'),
    ],
    [],
  )

  const handleChangeCurator = useCallback(
    value => {
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        curatorId: value,
      })
    },
    [setQueryParams],
  )

  const handleChangeQa = useCallback(
    value => {
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        qaId: value,
      })
    },
    [setQueryParams],
  )

  const handleSetTierValue = useCallback(
    value => {
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        tier: value,
      })
    },
    [setQueryParams],
  )

  const handleChangeIsAvailableForWork = useCallback(
    ({ target: { checked } }) => {
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        isAvailableForWork: checked,
      })
    },
    [setQueryParams],
  )

  const handleChangeWasDoneInThePreviousTwoWeeks = useCallback(
    ({ target: { checked } }) => {
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        wasDoneInPreviousTwoWeeks: checked,
      })
    },
    [setQueryParams],
  )

  const handleChangeIsNotChecked = useCallback(
    ({ target: { checked } }) => {
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        isNotChecked: checked,
      })
    },
    [setQueryParams],
  )

  const handleChangeDishHasErrors = useCallback(
    ({ target: { checked } }) => {
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        hasErrors: checked,
      })
    },
    [setQueryParams],
  )

  const handleChangeDishIds = useCallback(
    value =>
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        dishIds: value,
        shouldScrollToDish: null,
      }),
    [setQueryParams],
  )

  const handleChangeDishTypeId = useCallback(
    value => {
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        dishTypeId: value,
      })
    },
    [setQueryParams],
  )

  const handleChangeCourseTypeId = useCallback(
    value => {
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        courseTypeId: value,
      })
    },
    [setQueryParams],
  )

  const handleChangeDishSearch = useCallback(
    value => {
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        search: value,
      })
    },
    [setQueryParams],
  )

  const handleChangeDishIsIgnored = useCallback(
    value => {
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        isDishTypeIgnored: value,
      })
    },
    [setQueryParams],
  )

  const handleChangeDishCertainty = useCallback(
    value => {
      setQueryParams({
        currentPage: PAGINATION.DEFAULT_PAGE,
        certainty: value,
      })
    },
    [setQueryParams],
  )

  const isDishesCuratedByFilterDisabled = useMemo(
    () => taskStatus !== MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value,
    [taskStatus],
  )

  const isDishesQadByFilterDisabled = useMemo(
    () => taskStatus !== MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value,
    [taskStatus],
  )

  const isAvailableForWorkFilterDisabled = useMemo(
    () =>
      isMenuCurationTaskDirty ||
      !includes(
        [
          MENU_CURATION_TASK_STATUS.TIER_ONE_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.CURATION_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value,
        ],
        taskStatus,
      ),
    [isMenuCurationTaskDirty, taskStatus],
  )

  const isWasDoneInPreviousTwoWeeksFilterDisable = useMemo(
    () =>
      isMenuCurationTaskDirty ||
      !includes(
        [
          MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value,
        ],
        taskStatus,
      ),
    [isMenuCurationTaskDirty, taskStatus],
  )

  const isNotCheckedFilterDisabled = useMemo(
    () =>
      isMenuCurationTaskDirty ||
      !includes(
        [
          MENU_CURATION_TASK_STATUS.TIER_ONE_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.CURATION_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.QA_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.QC_IN_PROGRESS.value,
        ],
        taskStatus,
      ),
    [isMenuCurationTaskDirty, taskStatus],
  )

  useEffect(() => {
    setDishIdValues(map(dishIds, toString))
  }, [dishIds])

  useEffect(() => {
    if (!isNil(menuId) && isTaskInProgress) {
      fetchDishCounts(filters)
    }
  }, [fetchDishCounts, filters, isTaskInProgress, menuId])

  return (
    <>
      <StyledPaddingBottomRow gutter={16}>
        <GridFormItem span={4} label="Dish Type">
          <Select
            showSearch
            allowClear
            value={dishTypeId}
            options={dishTypeFilterOptions}
            onChange={handleChangeDishTypeId}
          />
        </GridFormItem>
        <GridFormItem span={4} label="Course Type">
          <Select
            showSearch
            allowClear
            value={courseTypeId}
            options={courseTypeFilterOptions}
            onChange={handleChangeCourseTypeId}
          />
        </GridFormItem>
      </StyledPaddingBottomRow>
      <StyledPaddingBottomRow gutter={16}>
        <GridFormItem span={3} label="Available for work">
          <Checkbox
            disabled={isAvailableForWorkFilterDisabled}
            checked={isBooleanTrue(selectedIsAvailableForWork)}
            onChange={handleChangeIsAvailableForWork}
          />
        </GridFormItem>
        <GridFormItem span={3} label="Only done in the last 2 weeks">
          <Checkbox
            disabled={isWasDoneInPreviousTwoWeeksFilterDisable}
            checked={isBooleanTrue(selectedWasDoneInPreviousTwoWeeks)}
            onChange={handleChangeWasDoneInThePreviousTwoWeeks}
          />
        </GridFormItem>
        <GridFormItem span={2} label="Is not checked">
          <Checkbox
            disabled={isNotCheckedFilterDisabled}
            checked={isBooleanTrue(isNotChecked)}
            onChange={handleChangeIsNotChecked}
          />
        </GridFormItem>
        <GridFormItem span={2} label="With errors">
          <Checkbox
            disabled={isMenuCurationTaskDirty}
            checked={isBooleanTrue(hasErrors)}
            onChange={handleChangeDishHasErrors}
          />
        </GridFormItem>
        <GridFormItem span={3} label="Tier">
          <Select
            allowClear
            showSearch
            placeholder="Select Tier"
            options={tierOptions}
            value={tier}
            onChange={handleSetTierValue}
          />
        </GridFormItem>
        <GridFormItem span={3} label="Certainty">
          <Select
            allowClear
            showSearch
            disabled={isMenuCurationTaskDirty}
            value={certainty}
            onChange={handleChangeDishCertainty}
            options={certaintyOptions}
          />
        </GridFormItem>
        <GridFormItem span={3} label="Ignored">
          <Select
            allowClear
            showSearch
            disabled={isMenuCurationTaskDirty}
            value={isDishTypeIgnored}
            onChange={handleChangeDishIsIgnored}
            options={ignoredOptions}
          />
        </GridFormItem>
        <GridFormItem span={5} label="Keyword search">
          <StyledFullWidthSearchInput
            placeholder="Search for dishes by keyword..."
            disabled={isMenuCurationTaskDirty}
            defaultValue={search}
            onSearch={handleChangeDishSearch}
          />
        </GridFormItem>
      </StyledPaddingBottomRow>
      <StyledPaddingBottomRow gutter={16}>
        <GridFormItem span={8} label="Dishes">
          <Select
            showSearch
            allowClear
            mode="multiple"
            value={dishIdValues}
            placeholder="Select dishes to filter"
            onChange={handleChangeDishIds}
            options={dishNameOptions}
          />
        </GridFormItem>
        <GridFormItem span={4} label="Curated by:">
          <MenuCurationTaskCuratorFilter
            menuId={menuId}
            selectedCurator={curatorId}
            onChange={handleChangeCurator}
            isDisabled={isDishesCuratedByFilterDisabled}
            wasDoneInPreviousTwoWeeks={selectedWasDoneInPreviousTwoWeeks}
          />
        </GridFormItem>
        <GridFormItem span={4} label="Qad by:">
          <MenuCurationTaskQaFilter
            menuId={menuId}
            selectedQa={qaId}
            onChange={handleChangeQa}
            isDisabled={isDishesQadByFilterDisabled}
            wasDoneInPreviousTwoWeeks={selectedWasDoneInPreviousTwoWeeks}
          />
        </GridFormItem>
        <GridFormItem span={8} label="Menu titles">
          <DishMenuTitleFilter isDisabled={isMenuCurationTaskDirty} />
        </GridFormItem>
      </StyledPaddingBottomRow>
    </>
  )
}

DishFilters.propTypes = {
  isMenuCurationTaskDirty: PropTypes.bool,
  curationDetailLevel: PropTypes.string,
  menuId: PropTypes.number.isRequired,
  taskStatus: PropTypes.string.isRequired,
}

DishFilters.defaultProps = {
  isMenuCurationTaskDirty: false,
  curationDetailLevel: undefined,
}

export default DishFilters
